import { ODSLoadingSpinner } from '@telekom-ods/react-ui-kit';
import { createContext, useState, useMemo, type ReactNode, type FC } from 'react';
import { useSelector } from 'react-redux';

interface GlobalLoaderContextType {
  loading: boolean;
  startContextLoading: () => void;
  stopContextLoading: () => void;
  setGlobalLoaderEnabled: (enabled: boolean) => void;
}

interface GlobalLoaderProviderProps {
  children: ReactNode;
}

interface RootState {
  status: {
    loading: boolean;
  };
}

const GlobalLoaderContext = createContext<GlobalLoaderContextType | undefined>(undefined);

const GlobalLoaderProvider: FC<GlobalLoaderProviderProps> = ({ children }) => {
  const reduxLoading = useSelector((state: RootState) => state.status.loading);
  const [activeContextLoaders, setActiveContextLoaders] = useState<number>(0);
  const [globalLoaderEnabled, setGlobalLoaderEnabled] = useState<boolean>(true);
  const contextLoading = activeContextLoaders > 0;
  const loading = reduxLoading || contextLoading;
  const contextValue = useMemo<GlobalLoaderContextType>(
    () => ({
      loading,
      startContextLoading: () => {
        setActiveContextLoaders(prev => prev + 1);
      },
      stopContextLoading: () => {
        setActiveContextLoaders(prev => Math.max(prev - 1, 0));
      },
      setGlobalLoaderEnabled,
    }),
    [loading]
  );

  return (
    <GlobalLoaderContext.Provider value={contextValue}>
      {globalLoaderEnabled && (
        <div
          style={{
            position: 'fixed',
            display: loading ? 'flex' : 'none',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(221, 221, 221, 0.47)',
            height: '100vh',
            width: '100vw',
            zIndex: 1000,
          }}
        >
          <ODSLoadingSpinner size='large' />
        </div>
      )}
      {children}
    </GlobalLoaderContext.Provider>
  );
};

export { GlobalLoaderContext, GlobalLoaderProvider };
export type { GlobalLoaderContextType };
